// Consultas IoT para lecturas agregadas por nave y el panel de demo.

import { apiFetch, qk, useApiMutation, useApiQuery } from './client';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth';
import { cacheReadThrough } from '@/lib/offline/cache';
import { currentSensorData } from '@/lib/iotPresentation';
import {
  applySensorEquipment,
  getSensorEquipment,
  type SensorEquipment,
} from '@/lib/sensorEquipment';
import { useNaves } from './naves';
import type { Nave } from '@/lib/types';

export type IoTSensorState = 'normal' | 'alert' | 'disconnected' | 'no_data';

export interface IoTReading {
  simulated?: boolean;
  sensor_id?: number | null;
  value: number | null;
  unit: string;
  state: IoTSensorState;
  alert_type: string | null;
  received_at?: string | null;
  measured_at?: string | null;
}

export interface IoTEnvironmentDevice {
  device_id: number | null;
  station?: string;
  temperature: IoTReading | null;
  humidity: IoTReading | null;
}

export interface NaveSensores {
  nave_id: number;
  environment: IoTEnvironmentDevice[];
  ammonia: IoTReading | null;
  water: IoTReading | null;
  silo: IoTReading | null;
  equipment?: SensorEquipment;
  available_environment?: string[];
  local_snapshot?: boolean;
  cached_at?: string;
}

export type IoTDemoScenario =
  | 'normal'
  | 'temperature_low'
  | 'temperature_high'
  | 'humidity_high'
  | 'ammonia_high'
  | 'water_stalled'
  | 'silo_low';

export interface IoTDemoScenarioItem {
  nave_id: number;
  scenarios: IoTDemoScenario[];
}

export interface IoTDemoScenariosResponse {
  items: IoTDemoScenarioItem[];
}

function sensorQuery(naveId: number | null, ownerId: number | null) {
  const key = [...qk('iot'), 'naves', naveId, 'sensores'];
  return {
    queryKey: [...key, 'owner', ownerId],
    queryFn: async () => {
      let loaded = false;
      const data = await cacheReadThrough(key, async () => {
        const result = await apiFetch<NaveSensores>(`/v1/iot/naves/${naveId}/sensores`);
        loaded = true;
        return result;
      });
      return loaded ? data : { ...data, local_snapshot: true };
    },
    networkMode: 'always' as const,
    enabled: naveId != null,
    staleTime: 30 * 1000,
    // La telemetría del simulador llega cada 60 s: refrescar cada 30 s
    // mantiene la tarjeta viva sin recargar la página.
    refetchInterval: 30 * 1000,
  };
}
function presented<
  T extends { data: NaveSensores | undefined; dataUpdatedAt: number; error: Error | null },
>(query: T, nave?: Nave) {
  // Leer dataUpdatedAt también actualiza la caducidad cuando el refresco conserva datos idénticos.
  const updatedAt = query.dataUpdatedAt;
  const denied = [401, 403].includes((query.error as { status?: number } | null)?.status ?? 0);
  const known = query.data !== undefined || nave?.sensor_equipment != null;
  return {
    ...query,
    data:
      denied || !known
        ? undefined
        : currentSensorData(
            applySensorEquipment(query.data, getSensorEquipment(nave, query.data), nave?.id),
            Math.max(Date.now(), updatedAt),
          ),
  };
}
export function useSensoresNave(naveId: number | null) {
  const ownerId = useAuthStore((s) => s.user?.id ?? null);
  const naves = useNaves();
  const denied = [401, 403].includes((naves.error as { status?: number } | null)?.status ?? 0);
  return presented(
    useQuery(sensorQuery(naveId, ownerId)),
    denied ? undefined : naves.data?.find((nave) => nave.id === naveId),
  );
}
/** Observa las mismas claves que Inicio y Detalle; sólo se monta al abrir avisos. */
export function useSensoresNaves(naveIds: number[]) {
  const ownerId = useAuthStore((s) => s.user?.id ?? null);
  const naves = useNaves();
  const denied = [401, 403].includes((naves.error as { status?: number } | null)?.status ?? 0);
  return useQueries({ queries: naveIds.map((id) => sensorQuery(id, ownerId)) }).map(
    (query, index) =>
      presented(query, denied ? undefined : naves.data?.find((nave) => nave.id === naveIds[index])),
  );
}

export function useIoTDemoScenarios() {
  return useApiQuery<IoTDemoScenariosResponse>({
    queryKey: [...qk('iot'), 'demo', 'scenarios'],
    queryFn: () => apiFetch<IoTDemoScenariosResponse>('/v1/iot/demo/scenarios'),
    staleTime: 0,
  });
}

export function useCambiarIoTDemoScenario() {
  return useApiMutation<{ naveId: number; scenarios: IoTDemoScenario[] }, IoTDemoScenarioItem>({
    mutationFn: ({ naveId, scenarios }, context) =>
      apiFetch<IoTDemoScenarioItem>(`/v1/iot/demo/naves/${naveId}/scenario`, {
        offlineBase: context.offlineBase,
        method: 'PUT',
        body: JSON.stringify({ scenarios }),
      }),
    invalidateKeys: [
      [...qk('iot'), 'demo', 'scenarios'],
      [...qk('iot'), 'naves'],
    ],
  });
}

// Estado de temperatura del detalle de nave, conservado para su ruta actual.
export interface TemperaturaNaveStatus {
  simulated?: boolean;
  nave_id: number;
  sensor_id: number | null;
  value: number | null;
  unit: string | null;
  measured_at: string | null;
  received_at: string | null;
  state: IoTSensorState;
  alert_type: 'TEMPERATURE_HIGH' | 'TEMPERATURE_LOW' | null;
  alert_opened_at: string | null;
  local_snapshot?: boolean;
  cached_at?: string;
}

export function useTemperaturaNave(naveId: number | null) {
  return useApiQuery<TemperaturaNaveStatus>({
    queryKey: [...qk('iot'), 'temperatura', naveId],
    queryFn: () => apiFetch<TemperaturaNaveStatus>(`/v1/iot/naves/${naveId}/temperatura`),
    enabled: naveId != null,
    staleTime: 30 * 1000,
  });
}
